/* Одна ротация refresh-токена на все вкладки.
 *
 * Внутри вкладки параллельные обновления и так схлопываются в один запрос (`inflightRefresh` в
 * `shared/api/client`). Между вкладками — нет: у каждой свой модуль, и на общем пробуждении
 * (вернулись к ноутбуку, сеть ожила) две-три вкладки шлют `/v1/auth/refresh` одновременно.
 *
 * Сервер такую гонку переживает — проигравший поворачивает от живой сессии (`grace_rotate`), — но
 * браузеру от каждого ответа прилетает свой `Set-Cookie`, и последним записывается тот, чей ответ
 * пришёл последним, а не тот, кто повернул токен последним. Если порядок разъедется, в cookie
 * останется уже отозванный токен, и следующий вход в админку начнётся с экрана входа. В базе прода
 * эта гонка видна невооружённым глазом: в семействе сессий владельца ротации регулярно идут парами
 * с разницей в доли секунды (разбор 17.09.2026).
 *
 * Здесь гонка снимается в корне: обновление идёт под межвкладочным замком, а победитель
 * рассказывает соседям новый access-токен. Вкладка, дождавшаяся замка, видит свежий токен соседа и
 * не ходит в сеть вовсе — ротация на пробуждение ровно одна.
 *
 * Оба API необязательные: без `navigator.locks` или `BroadcastChannel` (старые Safari, нестандартные
 * вебвью) всё работает как прежде — просто без координации.
 */

const LOCK_NAME = 'tt-auth-refresh';
const CHANNEL_NAME = 'tt-auth-refresh';

interface PeerToken {
  token: string;
  at: number;
}

/** `undefined` — канал ещё не создавали; `null` — в этом браузере его нет. */
let channel: BroadcastChannel | null | undefined;
let peer: PeerToken | null = null;

function ensureChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  if (typeof BroadcastChannel === 'undefined') {
    channel = null;
    return null;
  }
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent) => {
    const data = event.data as { token?: unknown } | null;
    if (data && typeof data.token === 'string') {
      // Токен соседа только запоминается — чужое состояние вкладки не трогаем: вкладка на экране
      // входа не должна вдруг оказаться с токеном в руках.
      peer = { token: data.token, at: Date.now() };
    }
  };
  return channel;
}

/** Выполнить обновление токена под общим для всех вкладок замком. */
export async function withRefreshLock<T>(run: () => Promise<T>): Promise<T> {
  ensureChannel();
  const locks = navigator.locks as LockManager | undefined;
  if (!locks) return run();
  return locks.request(LOCK_NAME, run);
}

/** Рассказать соседним вкладкам про свежий access-токен. */
export function announceAccessToken(token: string): void {
  ensureChannel()?.postMessage({ token });
}

/**
 * Токен, который сосед повернул ПОСЛЕ момента `since` — то есть пока мы ждали замок. Такой токен
 * свежее нашего, и своё обновление можно не делать.
 */
export function peerTokenSince(since: number): string | null {
  ensureChannel();
  return peer && peer.at >= since ? peer.token : null;
}

/** Только для тестов: забыть канал и токен соседа. */
export function resetCrossTabStateForTests(): void {
  channel?.close();
  channel = undefined;
  peer = null;
}
